import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  HStack,
  IconButton,
  SimpleGrid,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorMode,
  useToast,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { MdDarkMode, MdLightMode } from 'react-icons/md';
import {
  AppCard,
  AppInput,
  AppSelect,
  AppTextarea,
  AuthLoader,
  BrandLogo,
  BrandProgressBar,
  BrandSpinner,
  DangerButton,
  DashboardLoader,
  HubLoader,
  InlineLoader,
  OutlineButton,
  PageLoader,
  PrimaryButton,
  SecondaryButton,
  SegmentedPills,
  StatusAlertCard,
  StatusBadge,
  TableLoader,
  UploadTile,
} from '../../../components/ui';
import { useBrandPalette } from '../../../contexts/BrandPaletteContext';
import {
  spectrumColors,
  logoBrandColors,
  matchClarityColors,
  utkarshColors,
} from '../../../theme/colors';
import {
  brandBarMotion,
  primaryGradientMotion,
  secondaryGradientMotion,
} from '../../../theme/brandMotion';

function Section({ title, subtitle, children }) {
  return (
    <Box>
      <Heading size="md" mb={1}>
        {title}
      </Heading>
      {subtitle ? (
        <Text color="text.secondary" fontSize="14px" mb={4}>
          {subtitle}
        </Text>
      ) : (
        <Box mb={4} />
      )}
      {children}
    </Box>
  );
}

function ColorSwatch({ name, value }) {
  return (
    <VStack align="start" spacing={2}>
      <Box
        w="100%"
        h="56px"
        borderRadius="sm"
        bg={value}
        borderWidth="1px"
        borderColor="border.subtle"
      />
      <Text fontSize="13px" fontWeight="600" color="text.primary">
        {name}
      </Text>
      <Text fontSize="12px" color="text.secondary" fontFamily="mono">
        {value}
      </Text>
    </VStack>
  );
}

function PaletteCard({
  id,
  title,
  description,
  active,
  onSelect,
  swatches,
  children,
}) {
  return (
    <Box
      as="button"
      type="button"
      textAlign="left"
      onClick={() => onSelect(id)}
      borderWidth="2px"
      borderColor={active ? 'utkarsh.amber' : 'border.subtle'}
      bg="bg.surface"
      borderRadius="md"
      p={4}
      cursor="pointer"
      transition="border-color 0.15s ease, transform 0.15s ease"
      _hover={{ borderColor: active ? 'utkarsh.amber' : 'text.secondary', transform: 'translateY(-1px)' }}
      w="100%"
      sx={{ '& > *': { pointerEvents: 'none' } }}
    >
      <Flex justify="space-between" align="start" gap={3} mb={3}>
        <Box>
          <Text fontWeight="700" fontSize="16px" color="text.primary">
            {title}
          </Text>
          <Text fontSize="13px" color="text.secondary" mt={1}>
            {description}
          </Text>
        </Box>
        <StatusBadge status={active ? 'verified' : 'neutral'}>
          {active ? 'Active' : 'Preview'}
        </StatusBadge>
      </Flex>
      {children}
      <SimpleGrid columns={{ base: 4, sm: 8 }} spacing={2} mt={3}>
        {swatches.map((s) => (
          <Box
            key={s.name}
            h="28px"
            borderRadius="sm"
            bg={s.value}
            borderWidth="1px"
            borderColor="border.subtle"
            title={`${s.name} ${s.value}`}
          />
        ))}
      </SimpleGrid>
    </Box>
  );
}

export default function ThemePage() {
  const { colorMode, toggleColorMode } = useColorMode();
  const { paletteId, setPaletteId } = useBrandPalette();
  const toast = useToast();
  const [pill, setPill] = useState('Proprietorship');
  const [fuels, setFuels] = useState(['Diesel']);
  const [buDemo, setBuDemo] = useState('');

  const activeTokens =
    paletteId === 'logoBrand'
      ? logoBrandColors
      : paletteId === 'matchClarity'
        ? matchClarityColors
        : paletteId === 'spectrum'
          ? spectrumColors
          : utkarshColors;

  const paletteLabel =
    paletteId === 'logoBrand'
      ? 'Logo Brand'
      : paletteId === 'matchClarity'
        ? 'Merchant Ops'
        : paletteId === 'spectrum'
          ? 'Brand Spectrum'
          : 'Classic amber';

  const lightTokens = [
    { name: 'primary', value: activeTokens.amber },
    { name: 'ink', value: activeTokens.ink },
    { name: 'bgWarm', value: activeTokens.bgWarm },
    { name: 'inputBg', value: activeTokens.inputBg },
    { name: 'border', value: activeTokens.border },
    { name: 'secondaryText', value: activeTokens.secondaryText },
    { name: 'placeholderGray', value: activeTokens.placeholderGray },
    { name: 'amberTint', value: activeTokens.amberTint },
    { name: 'success', value: activeTokens.success },
    { name: 'error', value: activeTokens.error },
    { name: 'warning', value: activeTokens.warning },
    { name: 'info', value: activeTokens.info },
  ];

  if (
    paletteId === 'logoBrand' ||
    paletteId === 'matchClarity' ||
    paletteId === 'spectrum'
  ) {
    lightTokens.splice(1, 0, { name: 'secondary', value: activeTokens.secondary });
    lightTokens.push(
      { name: 'cyan', value: activeTokens.cyan },
      { name: 'magenta', value: activeTokens.magenta },
      { name: 'navy', value: activeTokens.navy },
    );
  }

  if (paletteId === 'spectrum') {
    lightTokens.splice(
      0,
      0,
      { name: 'resources', value: spectrumColors.resources },
      { name: 'logistics', value: spectrumColors.logistics },
      { name: 'diversity', value: spectrumColors.diversity },
      { name: 'energy', value: spectrumColors.energy },
    );
  }

  const darkTokens = [
    { name: 'dark.bg', value: activeTokens.dark.bg },
    { name: 'dark.surface', value: activeTokens.dark.surface },
    { name: 'dark.inputBg', value: activeTokens.dark.inputBg },
    { name: 'dark.border', value: activeTokens.dark.border },
    { name: 'dark.text', value: activeTokens.dark.text },
    { name: 'dark.secondaryText', value: activeTokens.dark.secondaryText },
    { name: 'dark.amberTint', value: activeTokens.dark.amberTint },
  ];

  const classicSwatches = [
    { name: 'amber', value: utkarshColors.amber },
    { name: 'ink', value: utkarshColors.ink },
    { name: 'bg', value: utkarshColors.bgWarm },
    { name: 'input', value: utkarshColors.inputBg },
    { name: 'border', value: utkarshColors.border },
    { name: 'success', value: utkarshColors.success },
    { name: 'warning', value: utkarshColors.warning },
    { name: 'error', value: utkarshColors.error },
  ];

  const logoSwatches = [
    { name: 'primary', value: logoBrandColors.amber },
    { name: 'secondary', value: logoBrandColors.secondary },
    { name: 'cyan', value: logoBrandColors.cyan },
    { name: 'magenta', value: logoBrandColors.magenta },
    { name: 'green', value: logoBrandColors.success },
    { name: 'ink', value: logoBrandColors.ink },
    { name: 'bg', value: logoBrandColors.bgWarm },
    { name: 'tint', value: logoBrandColors.amberTint },
  ];

  const claritySwatches = [
    { name: 'primary', value: matchClarityColors.amber },
    { name: 'secondary', value: matchClarityColors.secondary },
    { name: 'info', value: matchClarityColors.info },
    { name: 'magenta', value: matchClarityColors.magenta },
    { name: 'green', value: matchClarityColors.success },
    { name: 'ink', value: matchClarityColors.ink },
    { name: 'bg', value: matchClarityColors.bgWarm },
    { name: 'tint', value: matchClarityColors.amberTint },
  ];

  const spectrumSwatches = [
    { name: 'resources', value: spectrumColors.resources },
    { name: 'logistics', value: spectrumColors.logistics },
    { name: 'diversity', value: spectrumColors.diversity },
    { name: 'energy', value: spectrumColors.energy },
    { name: 'ink', value: spectrumColors.ink },
    { name: 'mid', value: spectrumColors.secondaryText },
    { name: 'light', value: spectrumColors.placeholderGray },
    { name: 'white', value: spectrumColors.white },
  ];

  return (
    <VStack align="stretch" spacing={10} pb={10}>
      <Flex justify="space-between" align={{ base: 'start', md: 'center' }} gap={4} wrap="wrap">
        <Box>
          <Heading size="lg">Theme & Widgets</Heading>
          <Text color="text.secondary" mt={2} maxW="720px">
            Compare Classic, Logo Brand, Merchant Ops, and Brand Spectrum (green / blue / purple /
            orange + gradient CTAs). Saved in this browser — try Dashboard + Hub after you pick one.
          </Text>
        </Box>
        <HStack
          spacing={3}
          bg="bg.surface"
          borderWidth="1px"
          borderColor="border.subtle"
          borderRadius="md"
          px={3}
          py={2}
        >
          <Text fontSize="13px" fontWeight="600" color="text.secondary">
            {colorMode === 'light' ? 'Light' : 'Dark'}
          </Text>
          <IconButton
            aria-label="Toggle color mode"
            icon={colorMode === 'light' ? <MdDarkMode /> : <MdLightMode />}
            onClick={toggleColorMode}
            variant="ghostInk"
            size="sm"
          />
          <Switch
            isChecked={colorMode === 'dark'}
            onChange={toggleColorMode}
            colorScheme="yellow"
          />
        </HStack>
      </Flex>

      <Section
        title="Brand palette"
        subtitle="Pick a palette — the whole admin (buttons, inputs, sidebar accents) updates immediately"
      >
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4} mb={5}>
          <PaletteCard
            id="classic"
            title="Classic amber"
            description="Current live look — warm cream surfaces, amber CTAs, charcoal ink."
            active={paletteId === 'classic'}
            onSelect={setPaletteId}
            swatches={classicSwatches}
          />
          <PaletteCard
            id="logoBrand"
            title="Logo Brand"
            description="Sampled from the mark — azure primary, tangerine secondary, cyan / magenta accents."
            active={paletteId === 'logoBrand'}
            onSelect={setPaletteId}
            swatches={logoSwatches}
          >
            <Flex align="center" gap={3} mb={3} wrap="wrap">
              <BrandLogo variant="mark" h="40px" />
              <BrandLogo variant="full" h="36px" maxW="160px" />
            </Flex>
            <SimpleGrid columns={2} spacing={2} mb={1}>
              <Box>
                <Box h="36px" borderRadius="sm" bg={logoBrandColors.amber} />
                <Text fontSize="11px" fontWeight="700" mt={1}>
                  Primary
                </Text>
                <Text fontSize="11px" color="text.secondary" fontFamily="mono">
                  {logoBrandColors.amber}
                </Text>
              </Box>
              <Box>
                <Box h="36px" borderRadius="sm" bg={logoBrandColors.secondary} />
                <Text fontSize="11px" fontWeight="700" mt={1}>
                  Secondary
                </Text>
                <Text fontSize="11px" color="text.secondary" fontFamily="mono">
                  {logoBrandColors.secondary}
                </Text>
              </Box>
            </SimpleGrid>
          </PaletteCard>
          <PaletteCard
            id="matchClarity"
            title="Merchant Ops"
            description="From Shopify Polaris (green primary), Stripe Dashboard (cool greys + navy ink), Airbnb Babu teal (success). Built for admin work — verify, list, match."
            active={paletteId === 'matchClarity'}
            onSelect={setPaletteId}
            swatches={claritySwatches}
          >
            <Text fontSize="12px" color="text.secondary" mb={3} lineHeight="1.45">
              Shopify merchants manage sellers & listings in grey + green. Stripe keeps CTAs rare
              on cool surfaces. Airbnb uses teal for trust. Combined here for Utkarsh staff ops.
            </Text>
            <SimpleGrid columns={2} spacing={2} mb={1}>
              <Box>
                <Box h="36px" borderRadius="sm" bg={matchClarityColors.amber} />
                <Text fontSize="11px" fontWeight="700" mt={1}>
                  Primary (Polaris)
                </Text>
                <Text fontSize="11px" color="text.secondary" fontFamily="mono">
                  {matchClarityColors.amber}
                </Text>
              </Box>
              <Box>
                <Box h="36px" borderRadius="sm" bg={matchClarityColors.secondary} />
                <Text fontSize="11px" fontWeight="700" mt={1}>
                  Secondary (interactive)
                </Text>
                <Text fontSize="11px" color="text.secondary" fontFamily="mono">
                  {matchClarityColors.secondary}
                </Text>
              </Box>
            </SimpleGrid>
          </PaletteCard>
          <PaletteCard
            id="spectrum"
            title="Brand Spectrum"
            description="Green, blue, purple, and orange from the brand colour guide (~75% strength) with a deep charcoal sidebar. Primary buttons use the 4-stop gradient."
            active={paletteId === 'spectrum'}
            onSelect={setPaletteId}
            swatches={spectrumSwatches}
          >
            <Box
              h="14px"
              borderRadius="full"
              mb={3}
              bgImage={spectrumColors.brandBarGradient}
              borderWidth="1px"
              borderColor="border.subtle"
              title="Brand gradient bar"
              sx={brandBarMotion('8s')}
            />
            <SimpleGrid columns={2} spacing={2} mb={1}>
              <Box>
                <Box
                  h="36px"
                  borderRadius="sm"
                  bgImage={spectrumColors.buttonGradient}
                  sx={primaryGradientMotion('10s')}
                />
                <Text fontSize="11px" fontWeight="700" mt={1}>
                  Primary (soft drift)
                </Text>
                <Text fontSize="11px" color="text.secondary">
                  Green → Blue → Purple → Orange
                </Text>
              </Box>
              <Box>
                <Box
                  h="36px"
                  borderRadius="sm"
                  bgImage={spectrumColors.secondaryGradient}
                  sx={secondaryGradientMotion('12s')}
                />
                <Text fontSize="11px" fontWeight="700" mt={1}>
                  Secondary (soft reverse)
                </Text>
                <Text fontSize="11px" color="text.secondary">
                  Blue → Purple · opposite ease
                </Text>
              </Box>
            </SimpleGrid>
          </PaletteCard>
        </SimpleGrid>
        <Text fontSize="13px" color="text.secondary">
          Active palette:{' '}
          <Text as="span" fontWeight="700" color="text.primary">
            {paletteLabel}
          </Text>
          . Mobile and landing keep their own styling until you decide to roll one out project-wide.
        </Text>
      </Section>

      <Divider borderColor="border.subtle" />

      <Section title="Color tokens" subtitle="Tokens for the active palette">
        <Text fontSize="13px" fontWeight="600" mb={3} color="text.secondary">
          Light
        </Text>
        <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 6 }} spacing={4} mb={6}>
          {lightTokens.map((t) => (
            <ColorSwatch key={t.name} {...t} />
          ))}
        </SimpleGrid>
        <Text fontSize="13px" fontWeight="600" mb={3} color="text.secondary">
          Dark
        </Text>
        <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 6 }} spacing={4}>
          {darkTokens.map((t) => (
            <ColorSwatch key={t.name} {...t} />
          ))}
        </SimpleGrid>
      </Section>

      <Divider borderColor="border.subtle" />

      <Section
        title="Typography"
        subtitle={
          paletteId === 'matchClarity'
            ? 'Inter — same workhorse as Shopify / Stripe-style admin UIs'
            : 'Inter — Bold headings, SemiBold labels, Regular body'
        }
      >
        <AppCard>
          <VStack align="start" spacing={3}>
            <Text fontSize="28px" fontWeight="700" color="text.primary">
              Display 28 / Bold
            </Text>
            <Text fontSize="26px" fontWeight="700" color="text.primary">
              Headline 26 / Bold
            </Text>
            <Text fontSize="18px" fontWeight="700" color="text.primary">
              Title 18 / Bold
            </Text>
            <Text fontSize="16px" fontWeight="600" color="text.primary">
              Title medium 16 / SemiBold
            </Text>
            <Text fontSize="16px" fontWeight="400" color="text.primary">
              Body large 16 / Regular — primary text
            </Text>
            <Text fontSize="14px" fontWeight="400" color="text.secondary">
              Body medium 14 / Regular — secondary text
            </Text>
            <Text fontSize="13px" fontWeight="400" color="text.secondary">
              Body small 13 / Regular — helper / captions
            </Text>
            <Text fontSize="14px" fontWeight="600" color="text.primary">
              Label 14 / SemiBold
            </Text>
          </VStack>
        </AppCard>
      </Section>

      <Divider borderColor="border.subtle" />

      <Section
        title="Buttons"
        subtitle={
          paletteId === 'spectrum'
            ? 'Primary eases green→blue→purple→orange · Secondary eases the other way (no flicker / hard loop).'
            : 'Primary = brand primary · Secondary = secondary accent'
        }
      >
        {paletteId === 'spectrum' ? (
          <Box
            h="10px"
            borderRadius="full"
            mb={4}
            maxW="420px"
            bgImage={spectrumColors.brandBarGradient}
            borderWidth="1px"
            borderColor="border.subtle"
            sx={brandBarMotion('8s')}
          />
        ) : null}
        <Wrap spacing={3}>
          <WrapItem>
            <PrimaryButton>Primary</PrimaryButton>
          </WrapItem>
          <WrapItem>
            <SecondaryButton>Secondary</SecondaryButton>
          </WrapItem>
          <WrapItem>
            <PrimaryButton isDisabled>Disabled</PrimaryButton>
          </WrapItem>
          <WrapItem>
            <OutlineButton>Outline</OutlineButton>
          </WrapItem>
          <WrapItem>
            <DangerButton>Log Out</DangerButton>
          </WrapItem>
          <WrapItem>
            <PrimaryButton size="sm">Small</PrimaryButton>
          </WrapItem>
        </Wrap>
      </Section>

      <Divider borderColor="border.subtle" />

      <Section
        title="Loading design"
        subtitle={
          paletteId === 'spectrum'
            ? 'Spectrum spinner, gradient progress, and card shimmer (green→blue→purple→orange). Same loaders power Hub, Classic, and auth.'
            : 'Spinners, skeletons, and shimmer follow the active palette. Switch to Brand Spectrum for the gradient ring + color shimmer.'
        }
      >
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5} mb={6}>
          <AppCard>
            <Text fontSize="13px" fontWeight="700" mb={4} color="text.secondary">
              Spinner + progress
            </Text>
            <Flex direction="column" align="center" gap={5} py={4}>
              <BrandSpinner size={72} label="Loading…" />
              <BrandProgressBar w="200px" h="5px" />
            </Flex>
          </AppCard>
          <AppCard>
            <Text fontSize="13px" fontWeight="700" mb={4} color="text.secondary">
              Inline / compact
            </Text>
            <InlineLoader label="Refreshing list…" py={8} />
          </AppCard>
        </SimpleGrid>

        <Text fontSize="13px" fontWeight="700" mb={3} color="text.secondary">
          Hub card skeleton
        </Text>
        <AppCard mb={6}>
          <HubLoader cards={4} />
        </AppCard>

        <Text fontSize="13px" fontWeight="700" mb={3} color="text.secondary">
          Dashboard skeleton
        </Text>
        <AppCard mb={6}>
          <DashboardLoader cards={3} />
        </AppCard>

        <Text fontSize="13px" fontWeight="700" mb={3} color="text.secondary">
          Table skeleton
        </Text>
        <AppCard mb={6} p={0} overflow="hidden">
          <TableLoader rows={4} columns={5} />
        </AppCard>

        <Text fontSize="13px" fontWeight="700" mb={3} color="text.secondary">
          Full page / auth (preview)
        </Text>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <Box
            borderWidth="1px"
            borderColor="border.subtle"
            borderRadius="md"
            overflow="hidden"
            bg="bg.canvas"
            minH="220px"
          >
            <PageLoader label="Loading page…" minH="220px" py={8} />
          </Box>
          <Box
            borderWidth="1px"
            borderColor="border.subtle"
            borderRadius="md"
            overflow="hidden"
            minH="220px"
            position="relative"
          >
            <Box transform="scale(0.72)" transformOrigin="top center" h="220px" overflow="hidden">
              <AuthLoader label="Signing you in…" />
            </Box>
          </Box>
        </SimpleGrid>
      </Section>

      <Divider borderColor="border.subtle" />

      <Section title="Inputs" subtitle="Filled input, accent focus ring">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
          <AppInput label="Trade / Agency name" placeholder="Enter trade or agency name" />
          <AppInput label="GST number" placeholder="GSTIN" />
          <AppSelect
            label="Business Unit"
            placeholder="Select business unit"
            value={buDemo}
            onChange={setBuDemo}
            options={[
              { value: 'apmul', label: 'APMuL' },
              { value: 'mstpl', label: 'MSTPL (EMC)' },
              { value: 'kcl', label: 'KCL' },
              { value: 'af', label: 'Adani Foundation' },
            ]}
          />
          <AppTextarea label="Notes" placeholder="Optional notes" rows={3} />
        </SimpleGrid>
      </Section>

      <Divider borderColor="border.subtle" />

      <Section title="Segmented pills" subtitle="Matches mobile constitution / fuel chips">
        <VStack align="stretch" spacing={5}>
          <SegmentedPills
            label="Constitution of business"
            options={['Proprietorship', 'Partnership', 'Private Limited']}
            value={pill}
            onChange={setPill}
          />
          <SegmentedPills
            label="Fuel type"
            options={['Diesel', 'Petrol', 'CNG', 'Electric']}
            multi
            values={fuels}
            onChange={setFuels}
          />
        </VStack>
      </Section>

      <Divider borderColor="border.subtle" />

      <Section title="Badges & status">
        <Wrap spacing={3} mb={5}>
          <WrapItem>
            <StatusBadge status="pending">Pending verification</StatusBadge>
          </WrapItem>
          <WrapItem>
            <StatusBadge status="verified">Verified</StatusBadge>
          </WrapItem>
          <WrapItem>
            <StatusBadge status="warning">Expiring soon</StatusBadge>
          </WrapItem>
          <WrapItem>
            <StatusBadge status="error">Rejected</StatusBadge>
          </WrapItem>
          <WrapItem>
            <StatusBadge status="info">Info</StatusBadge>
          </WrapItem>
          <WrapItem>
            <StatusBadge status="neutral">Draft</StatusBadge>
          </WrapItem>
        </Wrap>
        <StatusAlertCard
          title="Verification pending"
          body="Once management verifies business details, the vendor can raise equipment requests."
          footer="Our team typically verifies within 24 hours."
        />
      </Section>

      <Divider borderColor="border.subtle" />

      <Section title="Cards & upload tiles">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
          <AppCard>
            <Text fontWeight="700" mb={1}>
              Vendor card
            </Text>
            <Text fontSize="13px" color="text.secondary" mb={3}>
              Vendor · APMuL
            </Text>
            <StatusBadge status="pending">Pending verification</StatusBadge>
          </AppCard>
          <AppCard>
            <Text fontWeight="700" mb={1}>
              Villager card
            </Text>
            <Text fontSize="13px" color="text.secondary" mb={3}>
              Villager · Equipment owner
            </Text>
            <StatusBadge status="verified">Verified</StatusBadge>
          </AppCard>
        </SimpleGrid>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
          <UploadTile label="GST certificate" />
          <UploadTile label="Business photo" />
          <UploadTile label="Aadhaar front" />
          <UploadTile label="Aadhaar back" />
        </SimpleGrid>
      </Section>

      <Divider borderColor="border.subtle" />

      <Section title="Form controls">
        <HStack spacing={8} wrap="wrap">
          <Checkbox defaultChecked>Remember me</Checkbox>
          <Checkbox>Send SMS</Checkbox>
          <FormControl display="flex" alignItems="center" w="auto">
            <FormLabel mb="0" mr={3}>
              Notifications
            </FormLabel>
            <Switch defaultChecked colorScheme="yellow" />
          </FormControl>
        </HStack>
      </Section>

      <Divider borderColor="border.subtle" />

      <Section title="Table" subtitle="Admin list pattern">
        <AppCard p={0} overflow="hidden">
          <Box overflowX="auto">
            <Table variant="simple" size="md">
              <Thead bg="bg.input">
                <Tr>
                  <Th>Name</Th>
                  <Th>Role</Th>
                  <Th>Business Unit</Th>
                  <Th>Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td fontWeight="600">Ramesh Patel</Td>
                  <Td>Villager</Td>
                  <Td>—</Td>
                  <Td>
                    <StatusBadge status="verified">Verified</StatusBadge>
                  </Td>
                </Tr>
                <Tr>
                  <Td fontWeight="600">KK Agency</Td>
                  <Td>Vendor</Td>
                  <Td>APMuL</Td>
                  <Td>
                    <StatusBadge status="pending">Pending</StatusBadge>
                  </Td>
                </Tr>
                <Tr>
                  <Td fontWeight="600">MSTPL Contractor</Td>
                  <Td>Vendor</Td>
                  <Td>MSTPL (EMC)</Td>
                  <Td>
                    <StatusBadge status="warning">Docs missing</StatusBadge>
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </Box>
        </AppCard>
      </Section>

      <Divider borderColor="border.subtle" />

      <Section title="Toasts / feedback">
        <Wrap spacing={3}>
          <WrapItem>
            <PrimaryButton
              size="sm"
              onClick={() =>
                toast({
                  title: 'Saved',
                  description: 'Profile updated successfully.',
                  status: 'success',
                  duration: 3000,
                  isClosable: true,
                  position: 'top-right',
                })
              }
            >
              Success toast
            </PrimaryButton>
          </WrapItem>
          <WrapItem>
            <OutlineButton
              size="sm"
              onClick={() =>
                toast({
                  title: 'Warning',
                  description: 'GST certificate is still missing.',
                  status: 'warning',
                  duration: 3000,
                  isClosable: true,
                  position: 'top-right',
                })
              }
            >
              Warning toast
            </OutlineButton>
          </WrapItem>
          <WrapItem>
            <DangerButton
              size="sm"
              onClick={() =>
                toast({
                  title: 'Error',
                  description: 'Could not verify this account.',
                  status: 'error',
                  duration: 3000,
                  isClosable: true,
                  position: 'top-right',
                })
              }
            >
              Error toast
            </DangerButton>
          </WrapItem>
        </Wrap>
      </Section>

      <Divider borderColor="border.subtle" />

      <Section title="Layout reference">
        <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
          <AppCard>
            <Text fontSize="12px" color="text.secondary" mb={1}>
              Radius sm
            </Text>
            <Text fontWeight="600">12px — inputs / tiles</Text>
          </AppCard>
          <AppCard>
            <Text fontSize="12px" color="text.secondary" mb={1}>
              Radius md
            </Text>
            <Text fontWeight="600">14px — buttons / cards</Text>
          </AppCard>
          <AppCard>
            <Text fontSize="12px" color="text.secondary" mb={1}>
              Field gap
            </Text>
            <Text fontWeight="600">20px — form spacing</Text>
          </AppCard>
        </Grid>
      </Section>
    </VStack>
  );
}
